import { useState } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, X, GripVertical, Minimize2, Loader2, Copy, RotateCw, Trash2, FileType2 } from "lucide-react";
import { Field, TextInput, Select, PrimaryButton, GhostButton, FileDrop, ShareButton } from "@/tools/ui";
import { API } from "@/lib/api";
import { downloadBlob, formatBytes } from "@/lib/download";
import axios from "axios";

const FileRow = ({ file, onRemove, testid }) => (
  <div data-testid={testid} className="flex items-center gap-3 border border-border bg-card px-3 py-2.5">
    <GripVertical className="h-4 w-4 text-muted-foreground" />
    <span className="flex-1 truncate text-sm font-medium">{file.name}</span>
    <span className="text-xs text-muted-foreground">{formatBytes(file.size)}</span>
    <button onClick={onRemove} className="text-muted-foreground hover:text-[#E53935]"><X className="h-4 w-4" /></button>
  </div>
);

export const PdfMerge = () => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (files.length < 2) return toast.error("Add at least 2 PDFs");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/pdf/merge`, fd, { responseType: "blob" });
      downloadBlob(res.data, "merged.pdf");
      toast.success("PDFs merged");
    } catch (e) {
      toast.error("Merge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <FileDrop testid="merge-drop" accept="application/pdf" multiple label="Drop PDF files or click to upload" onFiles={(f) => setFiles((prev) => [...prev, ...f])} />
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => <FileRow key={i} testid={`merge-file-${i}`} file={f} onRemove={() => setFiles(files.filter((_, x) => x !== i))} />)}
        </div>
      )}
      <div className="flex gap-3">
        <PrimaryButton data-testid="merge-run" onClick={run} disabled={busy || files.length < 2}>{busy ? "Merging…" : "Merge PDFs"}</PrimaryButton>
        {files.length > 0 && <GhostButton data-testid="merge-clear" onClick={() => setFiles([])}><RotateCcw className="h-4 w-4" /> Clear</GhostButton>}
      </div>
    </div>
  );
};

export const PdfSplit = () => {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("");
  const [total, setTotal] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (f) => {
    setFile(f);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/pdf/info`, fd);
      setTotal(res.data.pages);
    } catch { setTotal(null); }
  };

  const run = async () => {
    if (!file || !pages.trim()) return toast.error("Enter pages to extract");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("pages", pages);
      const res = await axios.post(`${API}/pdf/extract`, fd, { responseType: "blob" });
      downloadBlob(res.data, "extracted.pdf");
      toast.success("Pages extracted");
    } catch { toast.error("Extract failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="split-drop" accept="application/pdf" label="Drop a PDF or click to upload" onFiles={(f) => onFile(f[0])} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name}{total ? ` · ${total} pages` : ""}</span>
            <GhostButton data-testid="split-reset" onClick={() => { setFile(null); setTotal(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <Field label="Pages to extract" hint="e.g. 1-3, 5, 8 (1-based)">
            <TextInput data-testid="split-pages" value={pages} onChange={(e) => setPages(e.target.value)} placeholder="1-3, 5" />
          </Field>
          <PrimaryButton data-testid="split-run" onClick={run} disabled={busy}>{busy ? "Extracting…" : "Extract Pages"}</PrimaryButton>
        </>
      )}
    </div>
  );
};

export const ImagesToPdf = () => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (files.length === 0) return toast.error("Add at least 1 image");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/pdf/images-to-pdf`, fd, { responseType: "blob" });
      downloadBlob(res.data, "images.pdf");
      toast.success("PDF created");
    } catch { toast.error("Conversion failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <FileDrop testid="i2p-drop" accept="image/*" multiple label="Drop images (JPG / PNG) or click to upload" onFiles={(f) => setFiles((prev) => [...prev, ...f])} />
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => <FileRow key={i} testid={`i2p-file-${i}`} file={f} onRemove={() => setFiles(files.filter((_, x) => x !== i))} />)}
        </div>
      )}
      <div className="flex gap-3">
        <PrimaryButton data-testid="i2p-run" onClick={run} disabled={busy || files.length === 0}>{busy ? "Building…" : "Create PDF"}</PrimaryButton>
        {files.length > 0 && <GhostButton data-testid="i2p-clear" onClick={() => setFiles([])}><RotateCcw className="h-4 w-4" /> Clear</GhostButton>}
      </div>
    </div>
  );
};

const b64ToBlob = (b64, type = "application/pdf") => {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type });
};

export const PdfCompress = () => {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/pdf/compress`, fd, { timeout: 120000 });
      setRes(r.data);
      toast.success("PDF compressed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compression failed");
    } finally {
      setBusy(false);
    }
  };

  const saved = res ? Math.max(0, Math.round((1 - res.compressed_size / res.original_size) * 100)) : 0;

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="compress-pdf-drop" accept="application/pdf" label="Drop a PDF or click to upload" onFiles={(f) => { setFile(f[0]); setRes(null); }} />
      ) : (
        <>
          <div className="flex items-center justify-between border border-border bg-muted px-4 py-3">
            <span className="text-sm font-medium truncate">{file.name} · {formatBytes(file.size)}</span>
            <GhostButton data-testid="compress-pdf-reset" onClick={() => { setFile(null); setRes(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
          </div>
          <PrimaryButton data-testid="compress-pdf-run" onClick={run} disabled={busy}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Compressing…</> : <><Minimize2 className="h-4 w-4" /> Compress PDF</>}
          </PrimaryButton>
          {res && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="border border-border bg-muted p-4"><p className="overline text-muted-foreground">Before</p><p className="mt-1 font-heading text-xl font-black">{formatBytes(res.original_size)}</p></div>
                <div className="border border-border bg-muted p-4"><p className="overline text-muted-foreground">After</p><p className="mt-1 font-heading text-xl font-black">{formatBytes(res.compressed_size)}</p></div>
                <div className="border border-primary bg-primary p-4 text-white"><p className="overline text-blue-200">Saved</p><p className="mt-1 font-heading text-xl font-black">{saved}%</p></div>
              </div>
              <PrimaryButton data-testid="compress-pdf-download" onClick={() => downloadBlob(b64ToBlob(res.pdf_base64), "compressed.pdf")}><Download className="h-4 w-4" /> Download</PrimaryButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Generic single-file backend tool that downloads a blob result
const BackendFileTool = ({ accept, endpoint, form = {}, filename, cta, testid, hint }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return toast.error("Upload a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await axios.post(`${API}${endpoint}`, fd, { responseType: "blob", timeout: 120000 });
      downloadBlob(res.data, filename(file));
      toast.success("Done — file downloaded");
    } catch (e) {
      toast.error("Conversion failed. Check the file format.");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid={`${testid}-drop`} accept={accept} label="Drop a file or click to upload" onFiles={(f) => setFile(f[0])} />
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name} · {formatBytes(file.size)}</span>
          <GhostButton data-testid={`${testid}-reset`} onClick={() => setFile(null)}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <PrimaryButton data-testid={`${testid}-run`} onClick={run} disabled={busy || !file}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</> : cta}
      </PrimaryButton>
    </div>
  );
};

const swapExt = (name, ext) => `${(name || "converted").replace(/\.[^.]+$/, "")}.${ext}`;

export const WordToPdf = () => <BackendFileTool testid="w2p" accept=".doc,.docx,.odt,.rtf" endpoint="/convert/office" form={{ target: "pdf" }} filename={(f) => swapExt(f.name, "pdf")} cta="Convert to PDF" hint="Supports DOC, DOCX, ODT, RTF." />;
export const ExcelToPdf = () => <BackendFileTool testid="x2p" accept=".xls,.xlsx,.ods,.csv" endpoint="/convert/office" form={{ target: "pdf" }} filename={(f) => swapExt(f.name, "pdf")} cta="Convert to PDF" hint="Supports XLS, XLSX, ODS, CSV." />;
export const PptToPdf = () => <BackendFileTool testid="p2p" accept=".ppt,.pptx,.odp" endpoint="/convert/office" form={{ target: "pdf" }} filename={(f) => swapExt(f.name, "pdf")} cta="Convert to PDF" hint="Supports PPT, PPTX, ODP." />;
export const PdfToWord = () => <BackendFileTool testid="p2w" accept="application/pdf,.pdf" endpoint="/pdf/to-word" filename={(f) => swapExt(f.name, "docx")} cta="Convert to Word" hint="Extracts text into an editable Word document (layout not preserved)." />;

export const PdfRotate = () => {
  const [file, setFile] = useState(null);
  const [angle, setAngle] = useState("90");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("angle", angle);
      const res = await axios.post(`${API}/pdf/rotate`, fd, { responseType: "blob" });
      downloadBlob(res.data, "rotated.pdf"); toast.success("Rotated");
    } catch { toast.error("Rotate failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      {!file ? <FileDrop testid="rotate-drop" accept="application/pdf" label="Drop a PDF or click to upload" onFiles={(f) => setFile(f[0])} /> : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name}</span>
          <GhostButton data-testid="rotate-reset" onClick={() => setFile(null)}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}
      <Field label="Rotation"><Select data-testid="rotate-angle" value={angle} onChange={(e) => setAngle(e.target.value)}><option value="90">90° clockwise</option><option value="180">180°</option><option value="270">270° (90° counter-clockwise)</option></Select></Field>
      <PrimaryButton data-testid="rotate-run" onClick={run} disabled={busy || !file}><RotateCw className="h-4 w-4" /> {busy ? "Rotating…" : "Rotate PDF"}</PrimaryButton>
    </div>
  );
};

export const PdfDeletePages = () => {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file || !pages.trim()) return toast.error("Upload a PDF and enter pages");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("pages", pages);
      const res = await axios.post(`${API}/pdf/delete-pages`, fd, { responseType: "blob" });
      downloadBlob(res.data, "result.pdf"); toast.success("Pages removed");
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      {!file ? <FileDrop testid="delpg-drop" accept="application/pdf" label="Drop a PDF or click to upload" onFiles={(f) => setFile(f[0])} /> : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name}</span>
          <GhostButton data-testid="delpg-reset" onClick={() => setFile(null)}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}
      <Field label="Pages to delete" hint="e.g. 2, 5-7 (1-based)"><TextInput data-testid="delpg-pages" value={pages} onChange={(e) => setPages(e.target.value)} placeholder="2, 5-7" /></Field>
      <PrimaryButton data-testid="delpg-run" onClick={run} disabled={busy || !file}><Trash2 className="h-4 w-4" /> {busy ? "Working…" : "Delete Pages"}</PrimaryButton>
    </div>
  );
};

export const PdfToTextTool = () => {
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return;
    setBusy(true); setText("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/pdf/to-text`, fd, { timeout: 60000 });
      setText(res.data.text); toast.success("Text extracted");
    } catch { toast.error("Extraction failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      {!file ? <FileDrop testid="p2t-drop" accept="application/pdf" label="Drop a PDF or click to upload" onFiles={(f) => setFile(f[0])} /> : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name}</span>
          <GhostButton data-testid="p2t-reset" onClick={() => { setFile(null); setText(""); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}
      <PrimaryButton data-testid="p2t-run" onClick={run} disabled={busy || !file}><FileType2 className="h-4 w-4" /> {busy ? "Extracting…" : "Extract Text"}</PrimaryButton>
      {text && (
        <div className="rounded-xl border border-border bg-muted">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="overline text-muted-foreground">Extracted Text</span>
            <div className="flex gap-2">
              <GhostButton data-testid="p2t-copy" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
              <ShareButton testid="p2t-share" text={text} />
            </div>
          </div>
          <pre data-testid="p2t-output" className="max-h-96 overflow-auto whitespace-pre-wrap px-4 py-4 text-sm">{text}</pre>
        </div>
      )}
    </div>
  );
};

